import React, {
    useState,
    useMemo,
    Dispatch,
    SetStateAction,
    useCallback
} from "react";
import styles from "./index.module.scss";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { useGenericMultiSelect } from "components/generic-multiselect/useGenericMultiSelect";
import {
    ActionsBarOptions,
    ComponentItem
} from "components/generic-multiselect/types";
import { useUserStatus } from "hooks/useUserStatus";
import { useAddVideosActions } from "components/add-videos/useAddVideoActions";
import { AddVideosContent } from "components/add-videos/AddVideosContent";
import { ModalBase } from "components/modal/ModalBase";
import { useGetPagedBroadcastByFilter } from "hooks/useGetPagedBroadcastByFilter";
import { BroadcastResponse } from "@switcherstudio/api-core-client";
import {
    BroadcastResponseDetails,
    BroadcastResponseDetailsProps
} from "components/entity-details/BroadcastResponseDetails";

interface SelectFeaturedTrailerModalProps {
    previouslySelectedBroadcastIds?: string[];
    setIsOpen?: Dispatch<SetStateAction<boolean>>;
    onSelect?: (selectedBroadcastIds: string[]) => void;
    playerId?: string;
    onClose?: (params?: any) => any;
}

export const SelectFeaturedTrailerModal = ({
    previouslySelectedBroadcastIds,
    setIsOpen,
    onSelect,
    playerId,
    onClose
}: SelectFeaturedTrailerModalProps) => {
    const [uploadSessionId, setUploadSessionId] = useState<string>(uuidv4());
    const [uploadModalKey, setUploadModalKey] = useState<number>(0);

    const { t } = useTranslation();
    const { userHasNoSubscription } = useUserStatus();

    /* Data fetching and handling */
    const {
        existingBroadcasts,
        firstLoading,
        reloading,
        refetchVideos,
        getNextPage,
        allLoaded,
        fetchBroadcastsWithNewParams
    } = useGetPagedBroadcastByFilter({
        videoStatusType: "LibraryOnDemand",
        itemsPerPage: 10,
        preFetch: () => {
            setUploadModalKey((prev) => prev + 1);
        }
    });

    const { handleDelete, actionsBarOptions, searchSortOptions } =
        useAddVideosActions({
            refetchVideos,
            isVideoLibrary: false,
            enableDelete: false
        });

    const broadcastListItemComponentItems: ComponentItem<BroadcastResponse>[] =
        useMemo(() => {
            if (!existingBroadcasts.length) return [];

            return existingBroadcasts.map((broadcast) => {
                const entitlements = [];
                return {
                    id: broadcast.Details.Id,
                    component: (
                        <BroadcastResponseDetails
                            broadcast={broadcast}
                            badges
                            entitlements={entitlements}
                            showPricing
                            showEdit={false}
                            showDownload
                            allowVideoPlaybackOnThumbnailClick
                            location="video-library"
                            handleDeleteBroadcast={() => {
                                handleDelete(broadcast, entitlements);
                            }}
                            onBroadcastUpdate={() => {
                                refetchVideos();
                            }}
                            disabled={broadcast?.Videos?.some((v) => !v)}
                        />
                    ),
                    baseObject: broadcast
                };
            });
        }, [existingBroadcasts, refetchVideos, handleDelete]);

    const { GenericMultiSelectComponent, handleSubmit, anyNewSelected } =
        useGenericMultiSelect<BroadcastResponse, BroadcastResponseDetailsProps>(
            {
                isMultiple: false,
                checkBoxLocation: "right",
                allowItemClick: true,
                items: broadcastListItemComponentItems,
                onSubmit: (ids) => {
                    if (onSelect && setIsOpen) {
                        onSelect(
                            ids.filter(
                                (id) =>
                                    !previouslySelectedBroadcastIds.includes(id)
                            )
                        );
                        setIsOpen(false);
                    }
                },
                previouslySelectedIds: previouslySelectedBroadcastIds,
                emptyStateCopy: t("video-library:no-videos-match"),
                actionsBarOptions:
                    actionsBarOptions as ActionsBarOptions<BroadcastResponseDetailsProps>,
                searchSortOptions,
                loading: firstLoading,
                scrollable: !allLoaded,
                fetchWithNewParams: fetchBroadcastsWithNewParams
            }
        );

    const handleScroll = useCallback(
        (e: React.UIEvent<HTMLDivElement>) => {
            const target = e.target as HTMLDivElement;

            // Check if the user has scrolled near the bottom of the list
            if (target.scrollTop >= target.clientHeight - 200) {
                getNextPage();
            }
        },
        [getNextPage]
    );

    const AddVideosContentComponent = useMemo(
        () => (
            <div
                className={`content ${styles["content"]}`}
                onScroll={handleScroll}
            >
                <AddVideosContent
                    isVideoLibrary={false}
                    refetchVideos={refetchVideos}
                    firstLoading={firstLoading}
                    reloading={reloading}
                    GenericMultiSelectComponent={GenericMultiSelectComponent}
                    t={t}
                    userHasNoSubscription={userHasNoSubscription}
                    videoUploadSessionId={uploadSessionId}
                    setVideoUploadSessionId={setUploadSessionId}
                    playerId={playerId}
                    onClose={onClose}
                    uploadModalKey={uploadModalKey}
                />
            </div>
        ),
        [
            refetchVideos,
            firstLoading,
            reloading,
            GenericMultiSelectComponent,
            t,
            userHasNoSubscription,
            uploadSessionId,
            setUploadSessionId,
            playerId,
            onClose,
            uploadModalKey,
            handleScroll
        ]
    );

    return (
        <ModalBase
            isOpen={true}
            setIsOpen={setIsOpen}
            header={t("player-status-page:modal-title")}
            onDismiss={() => setIsOpen(false)}
            primaryButton={{
                text:
                    previouslySelectedBroadcastIds?.length === 0
                        ? t("player-status-page:buttons:select-video")
                        : t("player-status-page:buttons:save-changes"),
                onClick: handleSubmit,
                disabled: !anyNewSelected
            }}
            secondaryButton={{
                text: t("buttons:cancel"),
                onClick: () => setIsOpen(false)
            }}
        >
            {AddVideosContentComponent}
        </ModalBase>
    );
};
